import React, { useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Switch,
  Platform,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useSettings } from '../contexts/SettingsContext';
import { useTutor } from '../contexts/TutorContext';
import { useChatContext } from '../contexts/ChatContext';
import { getTranslation } from '../i18n/translations';
import { Ionicons } from '@expo/vector-icons';

interface SettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  visible,
  onClose,
}) => {
  const { userInfo, setUserInfo, interfaceLanguage, setInterfaceLanguage } = useSettings();
  const { selectedTutor, setSelectedTutor } = useTutor();
  const { switchTutor } = useChatContext();

  const translations = {
    settingsTitle: getTranslation('settings', interfaceLanguage),
    languageLevel: getTranslation('languageLevel', interfaceLanguage),
    age: getTranslation('age', interfaceLanguage),
    nativeLanguage: getTranslation('nativeLanguage', interfaceLanguage),
    conversationType: getTranslation('conversationType', interfaceLanguage),
    speechSpeed: getTranslation('speechSpeed', interfaceLanguage),
    saveAndClose: getTranslation('saveAndClose', interfaceLanguage),
    tutor: getTranslation('tutor', interfaceLanguage) || 'Tutor'
  };

  const getOptionTranslation = (key: string) => getTranslation(key, interfaceLanguage);

  const languageLevels = ['beginner', 'intermediate', 'advanced', 'fluent'];
  const conversationTypes = ['casual', 'formal', 'business', 'academic'];
  const nativeLanguages = {
    'en': 'English',
    'pt': 'Portuguese',
    'fr': 'French',
    'es': 'Spanish',
    'de': 'German',
    'it': 'Italian',
    'ru': 'Russian'
  };

  const handleNativeLanguageChange = (languageName: string) => {
    // Update native language in user info
    setUserInfo(prev => ({ ...prev, nativeLanguage: languageName }));
    
    // Update interface language to match native language
    const languageCode = Object.entries(nativeLanguages).find(
      ([_, name]) => name === languageName
    )?.[0] as SupportedLanguage;
    
    if (languageCode) {
      setInterfaceLanguage(languageCode);
      console.log('Updating interface language to:', languageCode);
    }
  };

  const LabelWithIcon = ({ icon, label }: { icon: string, label: string }) => (
    <View style={styles.labelContainer}>
      <Ionicons name={icon} size={20} color="#666" style={styles.labelIcon} />
      <Text style={styles.configLabel}>{label}</Text>
    </View>
  );

  useEffect(() => {
    if (Platform.OS === 'web') {
      const style = document.createElement('style');
      style.textContent = `
        .custom-switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          margin-left: 12px;
        }
        
        .custom-switch input {
          opacity: 0;
          width: 0;
          height: 0;
          margin: 0;
          padding: 0;
          position: absolute;
        }
        
        .custom-switch .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #E0E0E0;
          transition: .4s;
          border-radius: 24px;
        }
        
        .custom-switch .slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }
        
        .custom-switch input:checked + .slider {
          background-color: #7B3FE4;
          box-shadow: 0 0 10px rgba(123, 63, 228, 0.3);
        }
        
        .custom-switch input:checked + .slider:before {
          transform: translateX(20px);
        }

        select {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          width: 100%;
          height: 48px;
          padding: 0 48px 0 16px;
          font-size: 16px;
          color: #333;
          background-color: #f5f5f5;
          background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%237B3FE4%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
          background-size: 14px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif';
        }

        select:hover {
          background-color: #efefef;
        }

        select:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(123, 63, 228, 0.2);
        }

        /* Hide default arrow in IE */
        select::-ms-expand {
          display: none;
        }

        /* Adjust select padding for Firefox */
        @-moz-document url-prefix() {
          select {
            padding-right: 40px;
          }
        }

        .gradient-button {
          background: linear-gradient(135deg, #7B3FE4 0%, #3b82f6 100%);
          position: relative;
          overflow: hidden;
          border-radius: 8px;
          padding: 10px 24px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: block;
          width: fit-content;
          margin: 20px auto;
        }

        .gradient-button:hover {
          box-shadow: 0 0 20px rgba(123, 63, 228, 0.4);
          transform: translateY(-1px);
        }

        .gradient-button:active {
          transform: translateY(1px);
        }

        .gradient-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #3b82f6 0%, #7B3FE4 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .gradient-button:hover::before {
          opacity: 1;
        }

        .gradient-button span {
          position: relative;
          z-index: 1;
        }

        .speech-speed-wrapper {
          position: relative;
          width: 100%;
          height: 24px;
          padding: 10px 0;
        }

        .speech-speed-track {
          position: absolute;
          left: 0;
          right: 0;
          top: 50%;
          height: 4px;
          background: #E0E0E0;
          border-radius: 2px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .speech-speed-progress {
          position: absolute;
          left: 0;
          top: 50%;
          height: 4px;
          background: #7B3FE4;
          border-radius: 2px;
          transform: translateY(-50%);
          pointer-events: none;
          transition: width 0.2s ease;
        }

        .speech-speed-input {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 24px;
          background: transparent;
          position: relative;
          cursor: pointer;
          touch-action: none;
          -webkit-tap-highlight-color: transparent;
          z-index: 1;
          margin: 0;
          padding: 0;
        }

        .speech-speed-input::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          margin-top: -8px;
          background: #7B3FE4;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 0 5px rgba(123, 63, 228, 0.3);
          position: relative;
          z-index: 1;
        }

        .speech-speed-input::-moz-range-thumb {
          width: 20px;
          height: 20px;
          margin-top: -8px;
          background: #7B3FE4;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 0 5px rgba(123, 63, 228, 0.3);
          position: relative;
          z-index: 1;
        }

        .speech-speed-input::-webkit-slider-runnable-track {
          width: 100%;
          height: 4px;
          background: transparent;
          border: none;
        }

        .speech-speed-input::-moz-range-track {
          width: 100%;
          height: 4px;
          background: transparent;
          border: none;
        }

        .speech-speed-input:focus {
          outline: none;
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    }
  }, []);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Ionicons name="settings-outline" size={24} color="#333" style={styles.headerIcon} />
            <Text style={styles.modalTitle}>{translations.settingsTitle}</Text>
            <TouchableOpacity style={styles.closeIcon} onPress={onClose}>
              <Ionicons name="close-outline" size={28} color="#666" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalScrollContent}>
            {/* Native Language */}
            <View style={styles.configField}>
              <LabelWithIcon icon="language-outline" label={translations.nativeLanguage} />
              <View style={styles.selectContainer}>
                <select 
                  value={Object.entries(nativeLanguages).find(([_, name]) => name === userInfo.nativeLanguage)?.[0] || 'en'}
                  onChange={(e) => handleNativeLanguageChange(nativeLanguages[e.target.value as SupportedLanguage])}
                  style={styles.selectInput}
                >
                  {Object.entries(nativeLanguages).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </View>
            </View>

            {/* Language Level */}
            <View style={styles.configField}>
              <LabelWithIcon icon="school-outline" label={translations.languageLevel} />
              <View style={styles.selectContainer}>
                <select 
                  value={userInfo.languageLevel}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, languageLevel: e.target.value }))}
                  style={styles.selectInput}
                >
                  {languageLevels.map((level) => (
                    <option key={level} value={level}>
                      {getOptionTranslation(level)}
                    </option>
                  ))}
                </select>
              </View>
            </View>

            {/* Age */}
            <View style={styles.configField}>
              <LabelWithIcon icon="person-outline" label={translations.age} />
              <View style={styles.selectContainer}>
                <select 
                  value={userInfo.age.toString()}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, age: parseInt(e.target.value) }))}
                  style={styles.selectInput}
                >
                  {Array.from({ length: 110 }, (_, i) => i + 1).map((age) => (
                    <option key={age} value={age}>{age}</option>
                  ))}
                </select>
              </View>
            </View>

            {/* Conversation Type */}
            <View style={styles.configField}>
              <LabelWithIcon icon="chatbubbles-outline" label={translations.conversationType} />
              <View style={styles.selectContainer}>
                <select 
                  value={userInfo.conversationType}
                  onChange={(e) => setUserInfo(prev => ({ ...prev, conversationType: e.target.value }))}
                  style={styles.selectInput}
                >
                  {conversationTypes.map((type) => (
                    <option key={type} value={type}>
                      {getOptionTranslation(type)}
                    </option>
                  ))}
                </select>
              </View>
            </View>

            {/* Speech Speed */}
            <View style={styles.configField}>
              <LabelWithIcon icon="speedometer-outline" label={translations.speechSpeed} />
              <View style={styles.sliderContainer}>
                <div className="speech-speed-wrapper">
                  <div className="speech-speed-track" />
                  <div 
                    className="speech-speed-progress" 
                    style={{ 
                      width: `${((parseFloat(userInfo.speechSpeed) - 0.5) / (2 - 0.5)) * 100}%` 
                    }} 
                  />
                  <input
                    type="range"
                    className="speech-speed-input"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={userInfo.speechSpeed}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserInfo(prev => ({ ...prev, speechSpeed: value }));
                    }}
                  />
                </div>
                <Text style={styles.sliderValue}>{userInfo.speechSpeed}x</Text>
              </View>
            </View>

            {/* Auto Play Audio */}
            <View style={styles.configField}>
              <View style={styles.switchContainer}>
                <LabelWithIcon icon="play-outline" label={getTranslation('autoPlayAudio', interfaceLanguage)} />
                {Platform.OS === 'web' ? (
                  <label className="custom-switch">
                    <input
                      type="checkbox"
                      checked={userInfo.autoPlayAudio}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setUserInfo(prev => ({
                          ...prev,
                          autoPlayAudio: newValue
                        }));
                      }}
                    />
                    <span className="slider"></span>
                  </label>
                ) : (
                  <Switch
                    trackColor={{ false: '#E0E0E0', true: '#7B3FE4' }}
                    thumbColor="#FFFFFF"
                    ios_backgroundColor="#E0E0E0"
                    onValueChange={(value) => {
                      setUserInfo(prev => ({
                        ...prev,
                        autoPlayAudio: value
                      }));
                    }}
                    value={userInfo.autoPlayAudio}
                    style={{ transform: Platform.OS === 'ios' ? [{ scaleX: 0.8 }, { scaleY: 0.8 }] : [] }}
                  />
                )}
              </View>
            </View>
          </ScrollView>

          <View style={styles.buttonContainer}>
            <TouchableOpacity 
              onPress={onClose}
              activeOpacity={0.9}
            >
              <div className="gradient-button">
                <span style={styles.saveButtonText}>Save</span>
              </div>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    position: 'relative',
  },
  headerIcon: {
    marginRight: 12,
  },
  closeIcon: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: [{ translateY: -14 }],
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#333',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  modalScrollContent: {
    padding: 20,
  },
  configField: {
    marginBottom: 24,
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  labelIcon: {
    marginRight: 8,
  },
  configLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  selectContainer: {
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    overflow: 'hidden',
    position: 'relative',
    marginTop: 8,
  },
  selectInput: {
    width: '100%',
    height: 48,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 16,
    fontSize: 16,
    color: '#333',
    border: 'none',
    outline: 'none',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  sliderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 8,
    height: 56,
  },
  sliderValue: {
    fontSize: 16,
    color: '#7B3FE4',
    minWidth: 45,
    fontWeight: '600',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 8,
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  option: {
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 8,
    minWidth: 120,
    textAlign: 'center',
  },
  selectedOption: {
    backgroundColor: '#7B3FE4',
  },
  optionText: {
    fontSize: 16,
    color: '#333',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  selectedOptionText: {
    color: 'white',
  },
  buttonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  saveButtonText: {
    color: 'white',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
});
